// ✅ src/pages/ClientsDebiteurs.js
import React, { useState, useEffect, useRef } from "react";
import {
  FaSpinner,
  FaSearch,
  FaFilter,
  FaChevronLeft,
  FaChevronRight,
  FaDollarSign,
  FaCreditCard,
  FaInfoCircle,
  FaTimes, // Icône pour fermer les modales
  FaTrashAlt, // Icône pour la suppression dans l'historique
  FaCheckCircle, // Icône de succès
  FaExclamationCircle, // Icône d'erreur
} from "react-icons/fa";
import "./ClientsDebiteurs.css";
// NOTE: Assurez-vous que le fichier config.js contient la bonne URL de base (par exemple, http://localhost:3001)
import API_URL from "../config";

// Constante pour le formatage monétaire
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "0 FCFA";
  return Number(amount).toLocaleString('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }) + " FCFA";
};

// --- Composant Modale de Message (Remplacement des alerts) ---
const MessageModal = ({ message, type, onClose }) => {
    const icon = type === 'success' ? <FaCheckCircle style={{ color: 'green', marginRight: '10px' }} /> : <FaExclamationCircle style={{ color: 'red', marginRight: '10px' }} />;
    const title = type === 'success' ? 'Succès' : 'Erreur/Information';

    return (
        <div className="modal-overlay">
            <div className="modal-content small-content">
                <button className="close-btn" onClick={onClose}><FaTimes /></button>
                <h4>{title}</h4>
                <p style={{ display: 'flex', alignItems: 'center' }}>
                    {icon} {message}
                </p>
                <div className="modal-actions">
                    <button onClick={onClose} style={{ backgroundColor: "gray" }}>
                        OK
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Composants Modals existants (mis à jour) ---

// 1. Modal de Paiement
const PaymentModal = ({ client, closeModal, handlePaymentSubmit, formatCurrency, showMessage }) => {
  const [montant, setMontant] = useState("");
  const [datePaiement, setDatePaiement] = useState(
    new Date().toISOString().substring(0, 10)
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const montantNum = Number(montant);
    if (isNaN(montantNum) || montantNum <= 0) {
      showMessage("Veuillez entrer un montant valide.", 'error');
      return;
    }
    handlePaymentSubmit(client.client_id, montantNum, datePaiement);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <button className="close-btn" onClick={closeModal}><FaTimes /></button>
        <h4>Enregistrer un Paiement pour **{client.client}**</h4>
        <p>Solde actuel: <span style={{ color: client.solde_client_actuel <= 0 ? 'red' : 'green' }}>{formatCurrency(client.solde_client_actuel)}</span></p>
        <form onSubmit={handleSubmit}>
          <label>
            Montant du paiement (FCFA):
            <input
              type="number"
              value={montant}
              onChange={(e) => setMontant(e.target.value)}
              required
              min="1"
            />
          </label>
          <label>
            Date du paiement:
            <input
              type="date"
              value={datePaiement}
              onChange={(e) => setDatePaiement(e.target.value)}
              required
            />
          </label>
          <div className="modal-actions">
            <button type="submit" style={{ backgroundColor: "green" }}>
              Ajouter le Paiement
            </button>
            <button type="button" onClick={closeModal} style={{ backgroundColor: "red" }}>
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// 2. Modal d'Historique
const HistoryModal = ({ client, history, loading, error, closeModal, formatCurrency, onDeletePayment, canTakePayment }) => {
  // État local pour la confirmation de suppression
  const [confirmDelete, setConfirmDelete] = useState(null); // Contient l'objet paiement à supprimer

  // Modal de confirmation de suppression (interne au composant HistoryModal)
  const ConfirmationModal = ({ paiement, onConfirm, onCancel }) => (
    <div className="modal-overlay small-overlay">
      <div className="modal-content small-content">
        <h4>Confirmer la suppression</h4>
        <p>Voulez-vous vraiment supprimer le paiement de **{formatCurrency(paiement.montant)}** du **{new Date(paiement.date_paiement).toLocaleDateString("fr-FR")}** ?</p>
        <div className="modal-actions">
          <button
            onClick={() => onConfirm(paiement)}
            style={{ backgroundColor: "red", color: "#fff", padding: "6px 12px", marginRight: "10px", borderRadius: "4px", border: "none" }}
          >
            Supprimer
          </button>
          <button
            onClick={onCancel}
            style={{ backgroundColor: "gray", color: "#fff", padding: "6px 12px", borderRadius: "4px", border: "none" }}
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );


  // Fonction pour gérer le clic sur la corbeille
  const handleInitiateDelete = (paiement) => {
    setConfirmDelete(paiement);
  };

  // Fonction pour confirmer et exécuter la suppression
  const handleConfirmDelete = (paiement) => {
    setConfirmDelete(null); // Fermer la modal de confirmation
    onDeletePayment(paiement.id); // Exécuter la suppression via la prop
  };
  
  return (
    <div className="modal-overlay">
      <div className="modal-content large-modal">
        <button className="close-btn" onClick={closeModal}><FaTimes /></button>
        <h4>Historique des Paiements pour **{client.client}**</h4>
        
        {loading && <p><FaSpinner className="spin" /> Chargement de l'historique...</p>}
        {error && <p style={{ color: "red" }}>Erreur: {error}</p>}
        
        {!loading && !error && (
          history.length === 0 ? (
            <p>Aucun paiement trouvé pour ce client.</p>
          ) : (
            <table className="history-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Montant (FCFA)</th>
                  {canTakePayment && <th>Action</th>} {/* Conditionné par canTakePayment */}
                </tr>
              </thead>
              <tbody>
                {history.map((p, i) => (
                  // IMPORTANT : Utiliser p.id comme clé unique pour chaque paiement
                  <tr key={p.id || i}> 
                    <td>{new Date(p.date_paiement).toLocaleDateString("fr-FR")}</td>
                    <td style={{ fontWeight: "bold", color: "green" }}>
                      {formatCurrency(p.montant)}
                    </td>
                    {canTakePayment && ( // Conditionné par canTakePayment
                      <td className="action-col-history">
                        <button
                          title="Supprimer ce paiement"
                          onClick={() => handleInitiateDelete(p)}
                          style={{ backgroundColor: 'transparent', color: 'red', border: 'none', cursor: 'pointer', fontSize: '1.2em' }}
                        >
                          <FaTrashAlt />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>
      {/* Afficher la modal de confirmation si un paiement est sélectionné pour suppression */}
      {confirmDelete && canTakePayment && ( // Utiliser canTakePayment pour la sécurité
        <ConfirmationModal
          paiement={confirmDelete}
          onConfirm={handleConfirmDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};


// MODIF: Ajout de 'typeUser' dans les props
export default function ClientsDebiteurs({ entrepriseId, siteId, typeUser }) {
  const [debiteurs, setDebiteurs] = useState([]);
  const [grouped, setGrouped] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [confirmArchive, setConfirmArchive] = useState(false);
  
  // États pour la fonctionnalité de paiement/info
  const [showPaymentModal, setShowPaymentModal] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  
  // NOUVEL ÉTAT POUR LA MODALE DE MESSAGE
  const [dialogMessage, setDialogMessage] = useState(null); // { message: "", type: "success" | "error" }

  // Fonction pour afficher le message
  const showMessage = (message, type = 'success') => {
      setDialogMessage({ message, type });
  };
  
  // Listes pour les filtres
  const [clientsList, setClientsList] = useState([]);
  const [produitsList, setProduitsList] = useState([]);
  const [denominationsList, setDenominationsList] = useState([]);

  const [filters, setFilters] = useState({
    client: "",
    produit: "",
    denomination1: "",
    denomination2: "",
    dateDebut: "",
    dateFin: "",
  });

  const wrapperRef = useRef(null);
  
  // 🎯 DÉFINITION DES PERMISSIONS D'ACTION
  // 1. Permission d'effectuer des paiements ou des suppressions (réservé aux non-entreprises)
  const canTakePayment = typeUser !== 'entreprise'; 
  // 2. Permission d'archiver (autorisé pour tous, y compris les entreprises)
  const canArchive = true; 


  // -----------------------------
  // 🔸 FONCTION DE RECHARGEMENT DES DONNÉES
  // -----------------------------
  const groupByClient = (list) => {
    const map = {};
    list.forEach((item) => {
      // Clé de groupement basée sur client_id
      const key = item.client_id || item.client || "—";
      
      if (!map[key]) {
        map[key] = { 
          client_id: item.client_id, 
          client: item.client || "—", 
          solde_client_actuel: item.solde_client_actuel || 0,
          dernier_paiement: 0,
          max_date_paiement: null, // Variable pour stocker la date la plus récente rencontrée
          items: [] 
        };
      }
      map[key].items.push(item);
      map[key].solde_client_actuel = item.solde_client_actuel;
      
      // ✅ LOGIQUE DE COMPARAISON DES DATES POUR LE TABLEAU (Utilise les données de l'API)
      // Si la ligne actuelle contient un paiement plus récent que celui déjà stocké, on met à jour
      if (item.dernier_paiement_date) {
          const currentDate = new Date(item.dernier_paiement_date);
          if (!map[key].max_date_paiement || currentDate > new Date(map[key].max_date_paiement)) {
              map[key].max_date_paiement = item.dernier_paiement_date;
              map[key].dernier_paiement = item.dernier_paiement_montant || 0;
          }
      } else if (!map[key].dernier_paiement && item.dernier_paiement_montant) {
          // Fallback au cas où il n'y a pas de date mais qu'un montant existe
          map[key].dernier_paiement = item.dernier_paiement_montant;
      }
    });
    return Object.values(map);
  };
  
  const loadDebiteurs = async (signal) => {
    setLoading(true);
    setError(null);
    try {
      // 1. Synchronisation des nouvelles dettes
      if (canTakePayment) {
        await fetch(`${API_URL}/api/debuteur/sync-clients-credit`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ entrepriseId, siteId }),
          signal,
        });
      }

      // 2. Chargement de la liste (incluant le solde client)
      const listRes = await fetch(
        `${API_URL}/api/debuteur/list?entrepriseId=${entrepriseId}&siteId=${siteId}`,
        { signal }
      );
      if (!listRes.ok) throw new Error("Erreur serveur lors du chargement");

      const data = await listRes.json();
      const arr = Array.isArray(data) ? data : [];

      setDebiteurs(arr);
      setGrouped(groupByClient(arr));

      // Mise à jour des listes de filtres
      setClientsList([...new Set(arr.map((d) => d.client))].filter(Boolean));
      setProduitsList([...new Set(arr.map((d) => d.produit_nom))].filter(Boolean));
      const uniqueDenoms = [
        ...new Set(
          arr.flatMap((d) => (d.denomination_stock || "").split(", ").map(den => den.split("(")[0].trim()))
        ),
      ].filter(Boolean);
      setDenominationsList(uniqueDenoms);

    } catch (err) {
      if (err.name !== "AbortError") {
        console.error(err);
        setError("Impossible de charger les clients débiteurs.");
      }
    } finally {
      setLoading(false);
    }
  };


  // -----------------------------
  // 🔸 Chargement initial
  // -----------------------------
  useEffect(() => {
    if (!entrepriseId || !siteId) return;
    const controller = new AbortController();
    loadDebiteurs(controller.signal);
  }, [entrepriseId, siteId, canTakePayment]); 

  // -----------------------------
  // 🔸 Application des filtres
  // -----------------------------
  const [filteredDebiteurs, setFilteredDebiteurs] = useState([]);
  const applyFilters = React.useCallback(() => { 
    let filtered = [...debiteurs];

    if (filters.client)
      filtered = filtered.filter((d) => d.client === filters.client);
    if (filters.produit)
      filtered = filtered.filter((d) => d.produit_nom === filters.produit);

    const { denomination1, denomination2 } = filters;
    
    // Logique de filtre sur les dénominations
    if (denomination1 || denomination2) {
        filtered = filtered.filter(d => {
            const stock = d.denomination_stock || "";
            let match1 = denomination1 ? stock.includes(denomination1) : false;
            let match2 = denomination2 ? stock.includes(denomination2) : false;

            if (denomination1 && denomination2) {
                return match1 || match2; // OU logique si les deux sont définis
            }
            return match1 || match2; // OU logique si seulement l'un est défini
        });
    }


    const { dateDebut, dateFin } = filters;
    if (dateDebut) filtered = filtered.filter((d) => d.date_vente && d.date_vente.substring(0, 10) >= dateDebut);
    if (dateFin) filtered = filtered.filter((d) => d.date_vente && d.date_vente.substring(0, 10) <= dateFin);

    if (search) {
      const val = search.toLowerCase();
      filtered = filtered.filter(
        (d) =>
          (d.client || "").toLowerCase().includes(val) ||
          (d.produit_nom || "").toLowerCase().includes(val) ||
          (d.denomination_stock || "").toLowerCase().includes(val) ||
          String(d.quantite).includes(val) ||
          String(d.dette_initiale).includes(val)
      );
    }

    setFilteredDebiteurs(filtered);
    setGrouped(groupByClient(filtered));
  }, [debiteurs, filters, search]); 

  useEffect(() => {
    applyFilters();
  }, [filters, search, debiteurs, applyFilters]);

  // -----------------------------
  // 🔸 Variables / Fonctions
  // -----------------------------

  const isFilterActive = Object.values(filters).some((v) => v) || search;

  const handleArchiveConfirmed = async () => {
    if (!canArchive) { 
        showMessage("Vous n'avez pas la permission d'effectuer cette action.", 'error');
        setConfirmArchive(false);
        return;
    }
    
    if (!filteredDebiteurs.length) return;
    try {
      const venteIds = filteredDebiteurs.map((d) => d.vente_id); 
      const res = await fetch(`${API_URL}/api/archive-debiteurs/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ venteIds, entrepriseId }), 
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Erreur lors de l'archivage");
      }

      setDebiteurs((prev) => prev.filter((d) => !venteIds.includes(d.vente_id)));
      setFilteredDebiteurs([]);
      setConfirmArchive(false);
      showMessage("Archivage terminé !", 'success');
    } catch (err) {
      console.error(err);
      showMessage("Erreur : " + err.message, 'error');
    }
  };

  const totalMontant = grouped
    .flatMap((g) => g.items)
    .reduce((acc, d) => acc + (Number(d.dette_initiale) || 0), 0);
  
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  
  const scrollBy = (amount = 300) => {
    const el = wrapperRef.current;
    if (!el) return;
    el.scrollBy({ left: amount, behavior: "smooth" });
  };

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const check = () => {
      setCanScrollLeft(el.scrollLeft > 10);
      setCanScrollRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 10);
    };
    check();
    el.addEventListener("scroll", check);
    window.addEventListener("resize", check);
    return () => {
      el.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, [grouped]);
  

  // --- 1. Soumission du Paiement ---
  const handlePaymentSubmit = async (clientId, montant, datePaiement) => {
    if (!canTakePayment) {
        showMessage("Vous n'avez pas la permission d'effectuer cette action.", 'error');
        setShowPaymentModal(null);
        return;
    }
    
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/paiement/enregistrer`, { 
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          clientId, 
          montant, 
          datePaiement, 
          entrepriseId, 
          siteId 
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Erreur lors de l'enregistrement du paiement.");
      }

      const result = await res.json();
      await loadDebiteurs();
      setShowPaymentModal(null);
      showMessage(`Paiement de ${formatCurrency(montant)} enregistré ! Nouveau solde: ${formatCurrency(result.nouveau_solde)}`, 'success');

    } catch (err) {
      console.error(err);
      showMessage("Erreur lors du paiement : " + err.message, 'error');
    } finally {
        setLoading(false);
    }
  };

  // --- 2. Récupération de l'historique ---
  const fetchPaymentHistory = async (client) => {
    setHistoryLoading(true);
    setHistoryError(null);
    setPaymentHistory([]);
    
    console.log("Appel historique pour Client ID:", client.client_id);

    try {
      const res = await fetch(`${API_URL}/api/paiement/historique?clientId=${client.client_id}&entrepriseId=${entrepriseId}&siteId=${siteId}`); 
      
      if (!res.ok) {
        throw new Error("Erreur lors de la récupération de l'historique.");
      }
      
      const data = await res.json();
      console.log("Données reçues de l'API (Historique):", data);

      if (data.length > 0) {
        console.log("Montant du paiement le plus récent trouvé:", data[0].montant);
        console.log("Date du paiement le plus récent:", data[0].date_paiement);
      } else {
        console.warn("L'historique est vide pour ce client.");
      }

      setPaymentHistory(Array.isArray(data) ? data : []);
      
    } catch (err) {
      console.error("Erreur détaillée lors du fetch historique:", err);
      setHistoryError(err.message || "Impossible de charger l'historique.");
    } finally {
      setHistoryLoading(false);
    }
  }

  const handleInfoClick = async (client) => {
    setShowHistoryModal(client);
    fetchPaymentHistory(client);
  };
  
  // --- 3. Suppression d'un paiement ---
  const handleDeletePayment = async (paiementId) => {
    if (!canTakePayment) {
        showMessage("Vous n'avez pas la permission d'effectuer cette action.", 'error');
        return;
    }
    
    if (!showHistoryModal) return; 
    
    try {
      setHistoryLoading(true);
      const res = await fetch(`${API_URL}/api/paiement/supprimer`, { 
        method: "DELETE", 
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          paiementId, 
          clientId: showHistoryModal.client_id, 
          entrepriseId, 
          siteId 
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Erreur lors de la suppression du paiement.");
      }
      
      showMessage("Paiement supprimé avec succès !", 'success');
      await fetchPaymentHistory(showHistoryModal);
      await loadDebiteurs();

    } catch (err) {
      console.error(err);
      showMessage("Erreur lors de la suppression : " + err.message, 'error');
    } finally {
      setHistoryLoading(false);
    }
  };

  // -----------------------------
  // 🔸 Rendu principal
  // -----------------------------
  if (loading)
    return (
      <div style={{ textAlign: "center", marginTop: 30 }}>
        <FaSpinner className="spin" /> Chargement…
      </div>
    );
  if (error) return <p style={{ color: "red", textAlign: "center" }}>{error}</p>;

  return (
    <div className="clients-debiteurs">
      <div className="header">
        <h3>Clients débiteurs</h3>
        <div className="search-box">
          <FaSearch className="icon" />
          <input
            type="text"
            placeholder="Rechercher…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="filters">
        <FaFilter /> <span>Filtres :</span>

        <select
          value={filters.client}
          onChange={(e) => setFilters({ ...filters, client: e.target.value })}
        >
          <option value="">Tous les clients</option>
          {clientsList.map((c, i) => (
            <option key={i} value={c}>
              {c}
            </option>
          ))}
        </select>

        <select
          value={filters.produit}
          onChange={(e) => setFilters({ ...filters, produit: e.target.value })}
        >
          <option value="">Tous les produits</option>
          {produitsList.map((p, i) => (
            <option key={i} value={p}>
              {p}
            </option>
          ))}
        </select>

        <select
          value={filters.denomination1}
          onChange={(e) =>
            setFilters({ ...filters, denomination1: e.target.value })
          }
        >
          <option value="">Dénomination 1</option>
          {denominationsList.map((d, i) => (
            <option key={i} value={d}>
              {d}
            </option>
          ))}
        </select>

        <input
          type="date"
          value={filters.dateDebut}
          onChange={(e) => setFilters({ ...filters, dateDebut: e.target.value })}
        />
        <input
          type="date"
          value={filters.dateFin}
          onChange={(e) => setFilters({ ...filters, dateFin: e.target.value })}
        />
      </div>

      {canArchive && isFilterActive && filteredDebiteurs.length > 0 && (
        <div style={{ margin: "10px 0" }}>
          <button
            className="btn-archive"
            style={{
              backgroundColor: "#007bff",
              color: "#fff",
              padding: "8px 16px",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
            onClick={() => setConfirmArchive(true)}
          >
            Archiver {filteredDebiteurs.length} débiteurs filtrés
          </button>
        </div>
      )}

      {confirmArchive && canArchive && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h4>Confirmer l'archivage</h4>
            <p>Êtes-vous sûr de vouloir archiver ces {filteredDebiteurs.length} débiteurs filtrés ?</p>
            <div className="modal-actions">
              <button
                onClick={handleArchiveConfirmed}
                style={{ backgroundColor: "green", color: "#fff", padding: "6px 12px", marginRight: "10px", borderRadius: "4px", border: "none" }}
              >
                Confirmer
              </button>
              <button
                onClick={() => setConfirmArchive(false)}
                style={{ backgroundColor: "red", color: "#fff", padding: "6px 12px", borderRadius: "4px", border: "none" }}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {showPaymentModal && canTakePayment && (
        <PaymentModal
          client={showPaymentModal}
          closeModal={() => setShowPaymentModal(null)}
          handlePaymentSubmit={handlePaymentSubmit}
          formatCurrency={formatCurrency}
          showMessage={showMessage}
        />
      )}

      {showHistoryModal && (
        <HistoryModal
          client={showHistoryModal}
          history={paymentHistory}
          loading={historyLoading}
          error={historyError}
          closeModal={() => setShowHistoryModal(null)}
          formatCurrency={formatCurrency}
          onDeletePayment={handleDeletePayment}
          canTakePayment={canTakePayment}
        />
      )}

      {dialogMessage && (
          <MessageModal 
              message={dialogMessage.message} 
              type={dialogMessage.type} 
              onClose={() => setDialogMessage(null)} 
          />
      )}

      {grouped.length === 0 ? (
        <p style={{ textAlign: "center", marginTop: 20 }}>
          Aucun client débiteur trouvé.
        </p>
      ) : (
        <div className="debiteurs-table-wrapper" ref={wrapperRef}>
          {canScrollLeft && (
            <button
              className="scroll-btn scroll-left"
              onClick={() => scrollBy(-320)}
              aria-label="Scroll left"
            >
              <FaChevronLeft />
            </button>
          )}
          {canScrollRight && (
            <button
              className="scroll-btn scroll-right"
              onClick={() => scrollBy(320)}
              aria-label="Scroll right"
            >
              <FaChevronRight />
            </button>
          )}

          <table className="debiteurs-table" role="table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Solde Actuel</th>
                <th>Montant Payé (Dernier)</th>
                <th>Produit</th>
                <th>Dénomination(s)</th>
                <th>Quantité</th>
                <th>Montant Vente (FCFA)</th>
                <th>Date vente</th>
                {canTakePayment && <th>Action</th>}
              </tr>
            </thead>
            <tbody>
              {grouped.map((g, gi) => (
                <React.Fragment key={gi}>
                  {g.items.map((item, idx) => (
                    <tr key={item.id || `${g.client}-${idx}`}>
                      {idx === 0 && (
                        <>
                          <td rowSpan={g.items.length} className="client-col">
                            <strong>{g.client}</strong>
                          </td>
                          <td rowSpan={g.items.length}>
                             <span style={{ color: g.solde_client_actuel <= 0 ? 'red' : 'green', fontWeight: 'bold' }}>
                                {formatCurrency(g.solde_client_actuel)}
                             </span>
                          </td>
                          <td rowSpan={g.items.length}>
                             {/* ✅ AFFICHAGE DU MONTANT PAYÉ LE PLUS RÉCENT */}
                             <span style={{ color: "blue", fontWeight: "bold" }}>
                                {formatCurrency(g.dernier_paiement)}
                             </span>
                          </td>
                        </>
                      )}
                      <td>{item.produit_nom || "—"}</td>
                      <td>{item.denomination_stock || "—"}</td>
                      <td>{item.quantite}</td>
                      <td style={{ color: "red", fontWeight: "bold" }}>
                        {formatCurrency(item.dette_initiale || item.montant)}
                      </td>
                      <td>
                        {item.date_vente
                          ? new Date(item.date_vente).toLocaleDateString("fr-FR")
                          : "—"}
                      </td>
                      {canTakePayment && idx === 0 && ( 
                        <td rowSpan={g.items.length} className="action-col">
                          {canTakePayment && (
                            <button
                              title="Enregistrer un paiement"
                              onClick={() => setShowPaymentModal(g)}
                              style={{ marginRight: 8, backgroundColor: '#4CAF50', color: 'white', border: 'none', padding: '6px', borderRadius: '4px', cursor: 'pointer' }}
                            >
                              <FaCreditCard /> Payer
                            </button>
                          )}
                          
                          <button
                            title="Voir l'historique des paiements"
                            onClick={() => handleInfoClick(g)}
                            style={{ backgroundColor: '#2196F3', color: 'white', border: 'none', padding: '6px', borderRadius: '4px', cursor: 'pointer' }}
                          >
                            <FaInfoCircle /> Info
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                  <tr className="client-separator">
                    <td colSpan={canTakePayment ? "9" : "8"}></td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="totals">
        <p>
          <strong>Total Montant Vente Affiché :</strong>{" "}
          {formatCurrency(totalMontant)}
        </p>
      </div>
    </div>
  );
}